import React, { useEffect, useRef, useState } from "react";
import {
  DropletOff,
  MinusCircle,
  AlertCircle,
  Droplets,
  MoveRight,
  Sparkles,
} from "lucide-react";
import { DIAGRAMA } from "@/content";
import Reveal from "@/components/Reveal";

const ICONS = {
  DropletOff,
  MinusCircle,
  AlertCircle,
  Droplets,
  MoveRight,
  Sparkles,
};

const COLON_PATH =
  "M52 172 L52 80 Q52 52 80 52 L124 52 Q152 52 152 80 L152 130 Q152 154 128 151 Q104 148 104 172 L104 190";

/** Ilustración estilizada y amigable del tracto digestivo (nunca hiperrealista) */
function ColonIllustration({ variant, play }) {
  const isFlow = variant === "flow";
  const tube = isFlow ? "rgba(139,197,63,0.22)" : "rgba(255,255,255,0.10)";
  const tubeStroke = isFlow ? "rgba(139,197,63,0.55)" : "rgba(255,255,255,0.22)";
  const accent = isFlow ? "var(--lima)" : "#B79AA6";

  return (
    <svg
      viewBox="0 0 200 210"
      className="w-full h-auto"
      style={{ maxWidth: 240 }}
      role="img"
      aria-label={
        isFlow
          ? "Ilustración del intestino con flujo continuo y parejo"
          : "Ilustración del intestino con desecho compactado y flujo entrecortado"
      }
    >
      {/* tubo */}
      <path
        d={COLON_PATH}
        fill="none"
        stroke={tube}
        strokeWidth={30}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={COLON_PATH}
        fill="none"
        stroke={tubeStroke}
        strokeWidth={32}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.5}
      />

      {isFlow ? (
        <path
          d={COLON_PATH}
          fill="none"
          stroke={accent}
          strokeWidth={12}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 620,
            strokeDashoffset: play ? 0 : 620,
            transition: "stroke-dashoffset 1600ms ease-out 700ms",
          }}
        />
      ) : (
        <path
          d={COLON_PATH}
          fill="none"
          stroke={accent}
          strokeWidth={11}
          strokeLinecap="round"
          strokeDasharray="16 22"
          style={{
            strokeDashoffset: play ? 0 : 620,
            opacity: play ? 0.85 : 0,
            transition: "stroke-dashoffset 1400ms ease-out, opacity 700ms ease-out",
          }}
        />
      )}

      {/* desecho compactado (solo columna A) */}
      {!isFlow &&
        [
          [52, 150],
          [52, 118],
          [52, 86],
          [86, 52],
          [118, 52],
          [152, 92],
        ].map(([cx, cy], i) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r={9}
            fill="#5B3B47"
            opacity={play ? 0.95 : 0}
            style={{ transition: `opacity 400ms ease-out ${300 + i * 90}ms` }}
          />
        ))}

      {/* burbujas suaves (solo columna B) */}
      {isFlow &&
        [
          [52, 150],
          [52, 100],
          [102, 52],
          [152, 105],
        ].map(([cx, cy], i) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r={5}
            fill="#ffffff"
            opacity={play ? 0.8 : 0}
            style={{ transition: `opacity 400ms ease-out ${900 + i * 120}ms` }}
          />
        ))}

      {/* flecha de salida */}
      <g
        opacity={play ? 1 : 0}
        style={{ transition: "opacity 500ms ease-out 1600ms" }}
      >
        <path
          d="M104 196 L104 204"
          stroke={accent}
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={isFlow ? "0" : "5 6"}
        />
        <path
          d="M97 200 L104 208 L111 200"
          fill="none"
          stroke={accent}
          strokeWidth={6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function Steps({ pasos, tone }) {
  return (
    <ul className="mt-6 space-y-3">
      {pasos.map((p, i) => {
        const Icon = ICONS[p.icon] || Droplets;
        return (
          <li key={p.text} className="flex items-start gap-3">
            <span
              className="flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 36,
                height: 36,
                background:
                  tone === "lima" ? "rgba(139,197,63,0.18)" : "rgba(255,255,255,0.08)",
              }}
            >
              <Icon
                size={19}
                strokeWidth={2}
                style={{ color: tone === "lima" ? "var(--lima)" : "#D8C3CC" }}
              />
            </span>
            <span
              className="text-[15px] md:text-[16px] leading-snug pt-1.5"
              style={{ color: tone === "lima" ? "#ffffff" : "rgba(255,255,255,0.72)" }}
            >
              {p.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function Diagrama() {
  const ref = useRef(null);
  const [playA, setPlayA] = useState(false);
  const [playB, setPlayB] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setPlayA(true);
      setPlayB(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setPlayA(true);
            setTimeout(() => setPlayB(true), 900);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-16 md:py-24"
      style={{ background: "linear-gradient(170deg, #6B1F3A 0%, #571831 100%)" }}
      data-testid="section-diagrama"
    >
      <div
        className="blob"
        style={{
          width: 380,
          height: 380,
          top: -80,
          right: -110,
          background: "rgba(139,197,63,0.22)",
        }}
      />
      <div
        className="blob"
        style={{
          width: 320,
          height: 320,
          bottom: -120,
          left: -100,
          background: "rgba(253,240,244,0.16)",
        }}
      />

      <div className="container-page relative z-[1]">
        <Reveal className="text-center max-w-[760px] mx-auto">
          <h2 className="h2-display text-white">{DIAGRAMA.h2}</h2>
        </Reveal>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
          {/* Columna A */}
          <Reveal
            className="p-6 md:p-8"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 24,
            }}
          >
            <span className="chip chip-dark">{DIAGRAMA.colA.titulo}</span>
            <div className="mt-6 flex justify-center">
              <ColonIllustration variant="slow" play={playA} />
            </div>
            <Steps pasos={DIAGRAMA.colA.pasos} tone="muted" />
          </Reveal>

          {/* Columna B */}
          <Reveal
            delay={80}
            className="p-6 md:p-8"
            style={{
              background: "rgba(139,197,63,0.10)",
              border: "2px solid rgba(139,197,63,0.45)",
              borderRadius: 24,
              boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
            }}
          >
            <span
              className="chip"
              style={{ background: "var(--lima)", color: "#2f4a10" }}
            >
              {DIAGRAMA.colB.titulo}
            </span>
            <div className="mt-6 flex justify-center">
              <ColonIllustration variant="flow" play={playB} />
            </div>
            <Steps pasos={DIAGRAMA.colB.pasos} tone="lima" />
          </Reveal>
        </div>

        <Reveal delay={160} className="mt-12 md:mt-16 text-center">
          <p
            className="font-display"
            style={{ fontSize: "clamp(30px, 7vw, 48px)", color: "#ffffff" }}
          >
            No empuja.{" "}
            <span style={{ color: "var(--lima)" }}>{DIAGRAMA.ancla.b}</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
